import React, { useState, useEffect } from 'react';
import { X, Zap } from 'lucide-react';
import { eventBus } from '../../core/EventBus';
import { signalingService } from '../../core/SignalingService';
import type { RacingStatus, LinkState, RaceReviewSnapshot } from './types';
import { DEFAULT_THRESHOLDS } from './types';
import LinkStatusBar from './LinkStatusBar';
import RacingProgress from './RacingProgress';
import ThresholdBar from './ThresholdBar';
import SessionRow from './SessionRow';
import WinnerSummary from './WinnerSummary';

interface IceRacingPanelProps {
  onClose: () => void;
  // 回看模式：传入快照（client+host）则展示快照，不订阅实时；不传=实时模式
  snapshot?: RaceReviewSnapshot | null;
  // 当前连接使用的 TURN（来自 stats，relay 时有值）
  clientTurn?: string;
  hostTurn?: string;
  relayProtocol?: string;
  transportType?: string;
}

const initialLink = (): LinkState => ({
  ws: signalingService.isConnected ? 'open' : 'connecting',
  host: signalingService.peerStatus, // PeerStatus('online'|'offline'|'waiting') 与 HostLinkState 同值集
  msgSent: 0,
  msgRecv: 0,
  lastMsgType: '',
  lastMsgAt: 0,
  serverReachable: signalingService.serverReachable,
});

const IceRacingPanel: React.FC<IceRacingPanelProps> = ({ onClose, snapshot, clientTurn, hostTurn, relayProtocol, transportType }) => {
  const reviewMode = !!snapshot;
  const [clientStatus, setClientStatus] = useState<RacingStatus | null>(snapshot?.client ?? null);
  const [hostStatus, setHostStatus] = useState<RacingStatus | null>(snapshot?.host ?? null);
  const [link, setLink] = useState<LinkState>(initialLink);
  const [nowTick, setNowTick] = useState<number>(Date.now());

  // 信令链路状态：实时模式与回看模式都订阅（链路区永远显示实时）
  useEffect(() => {
    const offOpen = eventBus.on('signaling:open', () => setLink(p => ({ ...p, ws: 'open', serverReachable: null })));
    const offClose = eventBus.on('signaling:close', (d) => setLink(p => ({ ...p, ws: 'closed', wsCloseCode: d.code })));
    const offErr = eventBus.on('signaling:error', () => setLink(p => ({ ...p, ws: 'closed' })));
    const offReach = eventBus.on('signaling:server_reachable', (d) => setLink(p => ({ ...p, serverReachable: d.reachable })));
    const offPeer = eventBus.on('peer:status', (d) => {
      if (d.peerType === 'Host') setLink(p => ({ ...p, host: d.status === 'online' ? 'online' : 'offline' }));
    });
    const offTimeout = eventBus.on('relay:peerTimeout', () => setLink(p => ({ ...p, host: 'waiting' })));
    const offMsg = eventBus.on('signaling:message', (d) => setLink(p => ({
      ...p,
      msgSent: p.msgSent + (d.dir === 'out' ? 1 : 0),
      msgRecv: p.msgRecv + (d.dir === 'in' ? 1 : 0),
      lastMsgType: d.type,
      lastMsgAt: Date.now(),
    })));
    return () => { offOpen(); offClose(); offErr(); offReach(); offPeer(); offTimeout(); offMsg(); };
  }, []);

  // ICE 状态：仅实时模式订阅
  useEffect(() => {
    if (reviewMode) return;
    const offClient = eventBus.on('race:status', (d) => setClientStatus(d));
    const offHost = eventBus.on('race:host_status', (d) => setHostStatus(d as unknown as RacingStatus));
    return () => { offClient(); offHost(); };
  }, [reviewMode]);

  // 活跃灯/最近消息时间需要持续刷新
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 500);
    return () => clearInterval(t);
  }, []);

  const status = clientStatus;
  const thresholds = status?.thresholds ?? DEFAULT_THRESHOLDS;
  const elapsedMs = Math.max(clientStatus?.elapsedMs ?? 0, hostStatus?.elapsedMs ?? 0);

  // 合并 client/host 候选（按 raceId，固定顺序，不重排）
  const raceIds: string[] = [];
  (clientStatus?.sessions ?? []).forEach(s => { if (!raceIds.includes(s.raceId)) raceIds.push(s.raceId); });
  (hostStatus?.sessions ?? []).forEach(s => { if (!raceIds.includes(s.raceId)) raceIds.push(s.raceId); });

  return (
    <div className="w-full glass-high rounded-2xl p-3 shadow-4xl border border-white/10">
      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-[10px] font-black tracking-widest text-zinc-500 uppercase flex items-center gap-2">
          <Zap size={12} className="text-amber-500" />
          ICE 详情{reviewMode && <span className="text-zinc-400 normal-case tracking-normal">· 回看</span>}
          {status?.winner && <span className="text-emerald-400 normal-case tracking-normal font-medium">· 完成</span>}
        </h3>
        <button onClick={onClose} className="p-1 hover:bg-white/5 rounded-full transition-colors">
          <X size={12} className="text-zinc-500" />
        </button>
      </div>

      {/* 链路区 */}
      <LinkStatusBar link={link} nowTick={nowTick} clientTurn={clientTurn} hostTurn={hostTurn} relayProtocol={relayProtocol} transportType={transportType} />

      {/* 进度（未完成时） */}
      {!status?.winner && status && (
        <div className="mb-2"><RacingProgress elapsedMs={elapsedMs} phase={status.phase} /></div>
      )}

      {/* 门槛行 */}
      <ThresholdBar thresholds={thresholds} />

      {/* 候选列表 */}
      {raceIds.length === 0 ? (
        <div className="text-center text-zinc-500 text-xs py-3">{reviewMode ? '暂无 race 记录' : '等待竞速数据...'}</div>
      ) : (
        <div className="space-y-1 max-h-[320px] overflow-y-auto custom-scrollbar">
          {raceIds.map(raceId => {
            const client = clientStatus?.sessions.find(s => s.raceId === raceId);
            const host = hostStatus?.sessions.find(s => s.raceId === raceId);
            const label = client?.label || host?.label || raceId;
            return <SessionRow key={raceId} label={label} client={client} host={host} thresholds={thresholds} raceCompleted={!!status?.winner} />;
          })}
        </div>
      )}

      {/* Winner 总结 */}
      {status && <WinnerSummary status={status} />}
    </div>
  );
};

export default IceRacingPanel;
